import { PermissionCode } from '@gb-ticket/shared-types';
import { Outlet, useMatch, useSearchParams } from 'react-router';

import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import { usePlatformMainMode } from '@/platform/layouts/platform-main-mode';
import { IntranetHeroChromeHost } from '@/products/intranet/components/intranet-home-mobile-chrome';
import { IntranetHeroBanner } from '@/products/intranet/components/intranet-hero';
import { ColleagueProfileProvider } from '@/products/intranet/features/intranet/colleague-profile-context';
import {
  INTRANET_MOBILE_ARTICLE_CHROME_CLASS,
  useIntranetChrome,
} from '@/products/intranet/features/intranet/use-intranet-chrome';
import {
  useIntranetAppearance,
  useIntranetCategories,
} from '@/products/intranet/features/intranet/use-intranet';
import {
  INTRANET_CHAT_PATH,
  INTRANET_EDITORIAL_APPEARANCE_PATH,
} from '@/products/intranet/intranet-paths';

/** Intranet page chrome: hero and mobile channel/search actions. Module nav lives in the platform sidebar. */
export function IntranetLayout() {
  const me = useMe();
  const { onHome, onArticle } = useIntranetChrome();
  const onAppearance = useMatch({ path: INTRANET_EDITORIAL_APPEARANCE_PATH, end: true });
  const onChatList = useMatch({ path: INTRANET_CHAT_PATH, end: true });
  const onChatThread = useMatch({ path: `${INTRANET_CHAT_PATH}/:conversationId`, end: true });
  const onChat = Boolean(onChatList ?? onChatThread);
  usePlatformMainMode(onChat ? 'fill' : 'default');
  const appearance = useIntranetAppearance();
  const categories = useIntranetCategories();
  const [params] = useSearchParams();
  const channelSlug = params.get('category');
  const channel = categories.data?.items.find((item) => item.slug === channelSlug);
  const bannerUrl = channel?.bannerUrl ?? null;
  const heroTitle = appearance.data?.title ?? 'Intranet';
  const canWrite = hasPermission(me.data, PermissionCode.INTRANET_CONTENT_WRITE);

  const hero = (
    <IntranetHeroBanner
      title={heroTitle}
      backgroundUrl={bannerUrl ?? appearance.data?.backgroundUrl ?? null}
      backgroundAlt={bannerUrl ? (channel?.name ?? null) : (appearance.data?.backgroundAlt ?? null)}
      editHref={canWrite && !onAppearance ? INTRANET_EDITORIAL_APPEARANCE_PATH : null}
    />
  );

  const chromeClass = onArticle ? INTRANET_MOBILE_ARTICLE_CHROME_CLASS : undefined;

  return (
    <ColleagueProfileProvider>
      {onChat ? (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col">
          <Outlet />
        </div>
      ) : (
        <div className="flex w-full min-w-0 flex-col gap-4">
          <IntranetHeroChromeHost hero={hero} showComposeFab={onHome} className={chromeClass}>
            <div className="min-w-0">
              <Outlet />
            </div>
          </IntranetHeroChromeHost>
        </div>
      )}
    </ColleagueProfileProvider>
  );
}
